// Live barcode detection off a RUNNING <video>, for the standalone capture page.
//
// ---------------------------------------------------------------------------
// WHY THIS IS NOT scannerCapabilities.js
// ---------------------------------------------------------------------------
// scannerCapabilities.js drives Shopify's NATIVE scanner — an OS overlay reached
// through App Bridge, which only exists inside the Shopify mobile admin and
// hands back `{ data: string }` with no image. It is the right answer in there,
// and it is unavailable out here: this page runs as a top-level document in a
// plain mobile browser, where there is no App Bridge at all.
//
// Out here we have the opposite trade. `getUserMedia` is NOT blocked (that is
// the whole reason this surface exists — see QuickScanner.jsx:2163, which skips
// the attempt inside the webview), so we can hold ONE camera stream and read
// barcodes off the frames it is already producing. No second camera, no phase
// change, no stream teardown between scanning and photographing. That is what
// makes "the barcode and the photos happen on one screen" possible.
//
// ---------------------------------------------------------------------------
// TWO DECODERS, SAME REASONING AS THE FILE PATH
// ---------------------------------------------------------------------------
// QuickScanner's decodeBarcodeFromFile already picks BarcodeDetector first and
// falls back to ZXing, because the Android WebView is Chromium and usually has
// the former. The same split applies here for a different reason: BarcodeDetector
// accepts a <video> element directly, so a detect costs one GPU read and no
// intermediate encode. ZXing needs pixels handed to it, which means a canvas
// draw and a JPEG round-trip per attempt — perfectly workable, several times
// more expensive, hence the slower cadence below.
//
// iOS Safari has no BarcodeDetector as of this writing, so on an iPhone the
// ZXing path is not a fallback, it is THE path. Do not let it rot.

/**
 * The formats we ask for, matching QuickScanner's list so the two surfaces
 * cannot disagree about what counts as a barcode. Retail packaging is
 * overwhelmingly EAN-13/UPC-A; the rest cost nothing to leave enabled.
 */
export const BARCODE_FORMATS = [
  "ean_13",
  "ean_8",
  "upc_a",
  "upc_e",
  "code_128",
  "code_39",
  "code_93",
  "codabar",
  "itf",
];

// ⚠️ NO qr_code, AND THAT IS THE POINT.
//
// A QR payload is a URL or arbitrary text; it can never be a retail product
// identifier, so nothing downstream can ever resolve one. Leaving it in the
// list did not add a capability, it added a way to LOSE a scan: packaging
// carries QR codes for recycling schemes, support pages and promotions, often
// larger and higher-contrast than the EAN a few centimetres away — so the
// detector would lock onto the wrong mark, the header would show something
// like an HP support URL, and the merchant would photograph a product against
// a barcode that matches nothing. One such row was filed during testing.
//
// The formats above are the ones retail packaging actually uses. Nothing else
// should be added without an answer to "what would we do with it".

// How often we look.
//
// ⚠️ THE GAP IS NOW MEASURED FROM THE START OF A TICK, not the end. It used to
// be scheduled after awaiting the work, so the real period was `work + gap` —
// and on the ZXing path the work was the larger of the two. That alone put iOS
// at roughly one attempt per second against Shopify's native ~30.
//
// Both are lower than they were because the work per attempt is now a fraction
// of what it was: see decodeZxing, which no longer round-trips through a JPEG,
// and drawFrame, which now crops rather than shrinking the whole sensor frame.
const INTERVAL_NATIVE_MS = 120;
const INTERVAL_ZXING_MS = 200;

// Scanning must never starve the page. Grabbing a frame off a 4K stream costs
// tens of ms of main thread (measured: ~400 ms of long tasks per second at 6x
// CPU throttle, 2 per second), so the gap after a tick is at least
// DUTY_FACTOR x what that tick cost: the scanner uses at most ~1/(1+factor) of
// the main thread however slow the phone is. And a pointerdown/touchstart
// pauses it for TOUCH_QUIET_MS so the tap's handler and paint run on a free
// thread. Neither changes WHAT is read, only when.
const DUTY_FACTOR = 1;
const MAX_GAP_MS = 400;
const TOUCH_QUIET_MS = 600;

/**
 * The width we hand the decoders, measured across the CROPPED band rather than
 * the whole sensor frame.
 *
 * This was 960 across the LONG EDGE of the full frame, on the reasoning that
 * "960px on the long edge keeps a typical EAN-13 well above the ~2px/module its
 * scanline needs". That reasoning assumed the barcode fills the frame. It does
 * not: the preview is object-fit:cover on a portrait screen, so most of a
 * 3840x2160 landscape frame is off-screen, and the merchant aims at a reticle
 * covering a fraction of what remains. A barcode filling that reticle arrived
 * at about 2.3 px/module — exactly at ZXing's floor, which is why it needed to
 * be held just so.
 *
 * Cropping to the band and sizing THAT to 960 gives roughly 9 px/module for the
 * same barcode, while handing the decoder ~10x fewer pixels.
 */
const DECODE_WIDTH = 960;

/**
 * The band we look in: the middle of the frame, where the reticle is.
 *
 * Barcodes are wide and short, so this is generous horizontally and tight
 * vertically. Every FULL_FRAME_EVERY-th attempt ignores it and scans the whole
 * frame, so a barcode the merchant has not centred is still found — slower, but
 * found, which matters more than the average case.
 */
// Matched to the reticle the merchant is actually aiming at, plus margin.
// .cap-reticle is min(82vw,420px) x min(24vh,190px); with the preview now
// letterboxed (object-fit:contain) the video occupies roughly 82% of a portrait
// screen's height and all of its width, so the reticle covers about 0.82 of the
// frame width and 0.29 of its height. These are a little looser than that, so
// that aiming slightly off still reads on the fast path.
const ROI = { widthFraction: 0.9, heightFraction: 0.3 };
const FULL_FRAME_EVERY = 5;

/**
 * A code locks only once enough reads in a row agree.
 *
 * One frame was enough, and on prod that filed checksum-valid wrong codes: an
 * EAN-8 decoded from inside an EAN-13 (19732060 for 5410316992060), or an
 * EAN-13 with the right half right and the left half wrong. A check digit
 * cannot catch either; a second frame disagreeing can. A different code resets
 * the count; a frame that reads nothing does not.
 *
 * EAN/UPC carry a check digit the decoder has already verified, so two reads
 * do. The other formats have no mandatory check and are the ones that misread
 * as each other, so they need three. Nothing is rejected: a short shelf code
 * still locks, it just has to read the same one more time.
 */
const RETAIL_FORMATS = new Set(["ean_13", "ean_8", "upc_a", "upc_e"]);
const CONFIRM_READS_RETAIL = 2;
const CONFIRM_READS_OTHER = 3;
const CONFIRM_WINDOW_MS = 3000;

/**
 * A loose-format read shaped like a GTIN whose check digit is wrong: the shape
 * of the Divine Ice Cream codes. It is usually a second code on the pack read
 * while the EAN beside it is too soft to decode, so it waits longer for the
 * EAN to show up. It still locks rather than being refused here; the backend's
 * gtin.js decides what a broken GTIN may create.
 */
const CONFIRM_READS_BROKEN_GTIN = 5;

/**
 * A Codabar read carrying fewer than 4 data characters is not a barcode.
 *
 * Codabar's start/stop characters are A-D, and printed text and graphics on a
 * pack decode as one: prod filed A8A, D3D, C$B, A/B against products that
 * carry real EANs (Patrón XO as C9C, Lindt as D0D). A still phone sees the same
 * phantom every frame, so agreeing reads do not stop it. No real shelf code in
 * prod has this shape; Code 39 and Code 128 codes of any length still pass.
 */
const CODABAR_MIN_DATA = 4;

function isPhantomCodabar({ code, format }) {
  if (format !== "codabar") return false;
  const data = /^[A-D].*[A-D]$/i.test(code) ? code.slice(1, -1) : code;
  return data.length < CODABAR_MIN_DATA;
}

function looksLikeBrokenGtin(code) {
  if (!/^(\d{8}|\d{12,14})$/.test(code)) return false;
  const digits = code.padStart(14, "0").split("").map(Number);
  const sum = digits.slice(0, 13).reduce((s, d, i) => s + d * (i % 2 === 0 ? 3 : 1), 0);
  return (10 - (sum % 10)) % 10 !== digits[13];
}

/**
 * Decides when a stream of per-frame reads is a barcode.
 *
 * Besides the agreeing streak, a code is held back while the recent window
 * holds a better explanation for it:
 *   - an EAN/UPC beats a loose-format read. Divine Ice Cream filed
 *     5060913144690 from three phones; an EAN reader cannot return that bad
 *     check digit, so a loose format decoded it off the pack.
 *   - a longer code ending in the same digits beats the shorter one, which is
 *     the EAN-8 read from inside an EAN-13.
 * Both only hold back: once the better read leaves the window, the code locks.
 *
 * The scanner keeps ONE CONFIRMER PER PASS, band and full frame. The full-frame
 * pass sees the neighbours too; sharing a window let a neighbour's EAN hold the
 * aimed shelf code back, and its reads reset the aimed code's streak every 5th
 * tick, so a code needing 5 reads never locked.
 *
 * `push(read, now)` takes `{ code, format }` and returns the read to lock on,
 * or null.
 */
export function createConfirmer() {
  let streakCode = null;
  let streakAt = 0;
  let streak = 0;
  let recent = [];

  const needed = ({ code, format }) => {
    if (RETAIL_FORMATS.has(format)) return CONFIRM_READS_RETAIL;
    return looksLikeBrokenGtin(code) ? CONFIRM_READS_BROKEN_GTIN : CONFIRM_READS_OTHER;
  };

  const outranked = (read) =>
    recent.some((r) => {
      if (r.code === read.code) return false;
      const readRetail = RETAIL_FORMATS.has(read.format);
      if (!readRetail && RETAIL_FORMATS.has(r.format)) return true;
      // Never the other way: an ITF-14 carrying the same GTIN would hold the
      // EAN while the EAN holds it, and neither would ever lock.
      if (readRetail && !RETAIL_FORMATS.has(r.format)) return false;
      // The tail rule is for an EAN-8/UPC-E read from inside an EAN-13. Two
      // loose shelf codes ending alike are just two codes.
      if (!readRetail) return false;
      const tail = read.code.slice(-Math.min(4, read.code.length));
      return r.code.length > read.code.length && r.code.endsWith(tail);
    });

  return {
    push(read, now) {
      if (isPhantomCodabar(read)) return null;
      recent = recent.filter((r) => now - r.at <= CONFIRM_WINDOW_MS);
      recent.push({ ...read, at: now });

      if (read.code === streakCode && now - streakAt <= CONFIRM_WINDOW_MS) {
        streak += 1;
      } else {
        streakCode = read.code;
        streak = 1;
      }
      streakAt = now;

      if (streak < needed(read) || outranked(read)) return null;
      return read;
    },
    reset() {
      streakCode = null;
      streak = 0;
      recent = [];
    },
  };
}

/**
 * The hints ZXing is built with, and both of them are load-bearing.
 *
 * POSSIBLE_FORMATS: without it MultiFormatReader loads FIVE readers — 1D, QR,
 * DataMatrix, Aztec and PDF417 — and runs all of them over every frame that
 * fails to decode, which is nearly every frame. Four of those are for
 * symbologies BARCODE_FORMATS deliberately excludes.
 *
 * TRY_HARDER: adds a 90-degree rotated pass and full-height row coverage.
 * Without it OneDReader scans 15 horizontal rows of the middle half and never
 * rotates — its own source says "we don't try rotation without the try harder
 * flag".
 *
 * ⚠️ MEASURED, because the obvious story about this was wrong. A first reading
 * of that source suggested any tilt would fail; it does not. Driving the real
 * page against a synthetic camera showing a real EAN-13, 5 trials per angle,
 * on the ZXing path (which is the iOS path):
 *
 *            BEFORE                  AFTER
 *     0deg   5/5, median 221ms       5/5, median 136ms
 *    20deg   0/5 never read          0/5 never read
 *    45deg   0/5 never read          0/5 never read
 *    80deg   0/5 never read          5/5, median 1548ms
 *    90deg   0/5 never read          5/5, median 1468ms
 *
 * So: small tilts always worked (a horizontal scanline still crosses every bar
 * of a tall barcode a few degrees off). What TRY_HARDER actually buys is the
 * VERTICAL case — a pack held sideways, previously unreadable at any speed,
 * now reads in about a second and a half.
 *
 * ⚠️ AND IT LEAVES A REAL HOLE: roughly 20 to 60 degrees fails, before and
 * after. ZXing scans rows and, with this flag, rows of the 90-degree rotation;
 * a diagonal barcode is neither. Nothing here fixes that, and the honest
 * mitigation is the reticle, which is landscape-shaped and so nudges people to
 * hold packs the way that works. Fixing it properly would mean decoding
 * additional rotated copies of the frame, at a cost per attempt that would
 * undo the speed this change just bought.
 */
export function zxingHints(BarcodeFormat, DecodeHintType) {
  return new Map([
    [
      DecodeHintType.POSSIBLE_FORMATS,
      [
        BarcodeFormat.EAN_13,
        BarcodeFormat.EAN_8,
        BarcodeFormat.UPC_A,
        BarcodeFormat.UPC_E,
        BarcodeFormat.CODE_128,
        BarcodeFormat.CODE_39,
        BarcodeFormat.CODE_93,
        BarcodeFormat.CODABAR,
        BarcodeFormat.ITF,
      ],
    ],
    [DecodeHintType.TRY_HARDER, true],
  ]);
}

/** Is the native detector usable in this browser? */
function hasNativeDetector() {
  // typeof, not `in`: a browser that blocks the API (Brave) or leaves a stub key behind
  // must read as "no detector", or the scanner skips the worker and runs ZXing on the main thread.
  return typeof window !== "undefined" && typeof window.BarcodeDetector === "function";
}

/**
 * Build a BarcodeDetector, tolerating a browser that has the constructor but
 * rejects our format list. Chromium ships formats behind availability, and a
 * throw here would silently disable scanning on a device that could scan.
 */
async function makeNativeDetector() {
  try {
    return new window.BarcodeDetector({ formats: BARCODE_FORMATS });
  } catch {
    try {
      return new window.BarcodeDetector();
    } catch {
      return null;
    }
  }
}

/**
 * The source rectangle of one attempt and the size it is decoded at. Pure, and
 * shared by the main-thread draw and the worker path's createImageBitmap.
 * Returns { sx, sy, sw, sh, w, h }, or null when the frame has no size yet.
 */
export function sourceRect(vw, vh, { full = false, band = null } = {}) {
  if (!vw || !vh) return null;

  // The source rectangle: the centre band, or the whole frame on the periodic
  // wide pass. Centred on both axes because that is where the reticle is and
  // where a person aims — unless the page says the reticle is elsewhere.
  let sw = full ? vw : Math.round(vw * ROI.widthFraction);
  let sh = full ? vh : Math.round(vh * ROI.heightFraction);
  let sx = Math.round((vw - sw) / 2);
  let sy = Math.round((vh - sh) / 2);
  if (!full && band) {
    sx = Math.round(vw * band.x);
    sy = Math.round(vh * band.y);
    sw = Math.max(1, Math.round(vw * band.w));
    sh = Math.max(1, Math.round(vh * band.h));
  }

  // Size by the WIDTH of what we took, not the long edge of the whole sensor
  // frame. A barcode is wide and short; its readability is px-per-module
  // across the band it sits in, and scaling by the frame's long edge was
  // shrinking the thing we came to read in order to fit sky and floor around
  // it. Never upscale — inventing pixels does not add detail, it adds ringing.
  const scale = Math.min(1, DECODE_WIDTH / sw);
  const w = Math.max(1, Math.round(sw * scale));
  const h = Math.max(1, Math.round(sh * scale));
  return { sx, sy, sw, sh, w, h };
}

/**
 * Draw the current video frame into a reused canvas, downscaled.
 *
 * ONE canvas for the life of the scanner, not one per attempt: at a decode
 * every 600ms a fresh canvas per tick is a steady stream of garbage on a device
 * that is also holding a camera stream and fifteen photographs.
 */
function drawFrame(video, canvas, { full = false, band = null } = {}) {
  const r = sourceRect(video.videoWidth, video.videoHeight, { full, band });
  if (!r) return false;
  const { sx, sy, sw, sh, w, h } = r;
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
  }
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  if (!ctx) return false;
  // Bilinear (the spec default, "low") aliases thin bars when downscaling 4x,
  // which is exactly what a barcode is made of.
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, w, h);
  return true;
}

/**
 * Of several codes in one frame, the one nearest where the merchant aims. The
 * full-frame pass sees the shelf too, and the neighbour's code used to win just
 * by being listed first.
 */
export function nearestCentre(results, canvas) {
  results = results?.filter((r) => r.rawValue);
  if (!results?.length) return null;
  const cx = canvas.width / 2;
  const cy = canvas.height / 2;
  const dist = (r) => {
    const b = r.boundingBox;
    if (!b) return Infinity;
    return Math.hypot(b.x + b.width / 2 - cx, b.y + b.height / 2 - cy);
  };
  return results.reduce((best, r) => (dist(r) < dist(best) ? r : best));
}

/**
 * Of the reads from one still photo, the code to use. Phantom Codabar reads are
 * dropped as on the live path; of what is left, the one nearest the centre.
 * Reads are `{ code, format, x, y }`; x and y are optional offsets from centre.
 */
export function pickUploadedRead(reads) {
  const ok = (reads || [])
    .map((r) => ({ ...r, code: String(r?.code ?? "").trim() }))
    .filter((r) => r.code && !isPhantomCodabar(r));
  if (!ok.length) return null;
  const d = (r) => Math.hypot(r.x || 0, r.y || 0);
  return ok.reduce((best, r) => (d(r) < d(best) ? r : best));
}

/**
 * Read a barcode off a photo the merchant chose: BarcodeDetector first, then
 * ZXing, the same pair as the live scanner. Tries the photo at two sizes, since
 * a full-resolution shot can defeat a decoder that a smaller one satisfies.
 * Resolves to the code, or null when there is none. Never throws.
 */
export async function decodeBarcodeFromImage(file) {
  let bitmap = null;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      return null;
    }
  }
  try {
    const detector = hasNativeDetector() ? await makeNativeDetector() : null;
    let zx = null;
    for (const maxSide of [1600, 1000]) {
      const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(bitmap.width * scale));
      canvas.height = Math.max(1, Math.round(bitmap.height * scale));
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);

      if (detector) {
        try {
          const found = (await detector.detect(canvas)).filter((r) => r.rawValue);
          const hit = pickUploadedRead(
            found.map((r) => {
              const b = r.boundingBox;
              return {
                code: r.rawValue,
                format: r.format || null,
                x: b ? b.x + b.width / 2 - canvas.width / 2 : 0,
                y: b ? b.y + b.height / 2 - canvas.height / 2 : 0,
              };
            }),
          );
          if (hit) return hit.code;
        } catch {
          /* fall through to ZXing */
        }
      }
      try {
        if (!zx) {
          const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] = await Promise.all([
            import("@zxing/browser"),
            import("@zxing/library"),
          ]);
          zx = {
            reader: new BrowserMultiFormatReader(zxingHints(BarcodeFormat, DecodeHintType)),
            name: (f) => BarcodeFormat[f]?.toLowerCase() || null,
          };
        }
        const res = zx.reader.decodeFromCanvas(canvas);
        const hit = pickUploadedRead([{ code: res?.getText(), format: zx.name(res.getBarcodeFormat()) }]);
        if (hit) return hit.code;
      } catch {
        /* nothing at this size */
      }
    }
    return null;
  } finally {
    bitmap.close?.();
  }
}

/**
 * A scanner bound to one <video>. Call start() when the stream is playing and
 * stop() when you lock a barcode or leave the page.
 *
 * `onResult(code, { format })` fires once createConfirmer says the reads agree.
 * It fires ONCE per start(): the loop stops itself on the first lock, because
 * everything downstream (locking the header, enabling capture) is a state
 * change the caller owns, and a second result arriving mid-transition is how
 * you end up photographing product A against product B's barcode.
 *
 * Every failure path is silent by design. A frame that does not decode is the
 * normal case, not an error — the merchant is still moving the phone.
 */
/**
 * The reticle, in the video FRAME's own fractions, when the preview fills the
 * screen with object-fit:cover. Cover crops the frame, so a box drawn on the
 * screen covers a different share of the sensor frame than it does of the
 * screen. The margin keeps a slightly-off aim on the fast path.
 *
 * rect and box are screen rects (left, top, width, height); vw/vh the frame.
 */
export function coverBand(rect, box, vw, vh, margin = { x: 0.04, y: 0.08 }) {
  if (!rect?.width || !box?.width || !box?.height || !vw || !vh) return null;
  const s = Math.max(box.width / vw, box.height / vh);
  const ox = box.left + (box.width - vw * s) / 2;
  const oy = box.top + (box.height - vh * s) / 2;
  const clamp = (v) => Math.min(1, Math.max(0, v));
  const x0 = clamp((rect.left - ox) / s / vw - margin.x);
  const y0 = clamp((rect.top - oy) / s / vh - margin.y);
  const x1 = clamp((rect.left + rect.width - ox) / s / vw + margin.x);
  const y1 = clamp((rect.top + rect.height - oy) / s / vh + margin.y);
  if (x1 <= x0 || y1 <= y0) return null;
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

// WARM-UP, called the moment the camera is requested. A browser without the
// platform reader (iOS Safari, Firefox) pays for the ZXing download and parse
// before the first frame can be read; starting it here overlaps that with the
// camera opening instead of following it. A browser WITH the platform reader
// (Chrome on Android) does nothing at all. The scanner adopts the worker below.
let prewarmed = null; // { w, msg, onmsg, onerr, failed }

function workerSupported() {
  return (
    typeof Worker === "function" &&
    typeof createImageBitmap === "function" &&
    typeof OffscreenCanvas === "function"
  );
}

export function warmBarcodeDecoder() {
  if (prewarmed || hasNativeDetector()) return;
  if (!workerSupported()) {
    // Main-thread ZXing: at least have the modules downloaded.
    Promise.all([import("@zxing/browser"), import("@zxing/library")]).catch(() => {});
    return;
  }
  try {
    const w = new Worker(new URL("./barcodeWorker.js", import.meta.url), { type: "module" });
    const st = { w, msg: null, onmsg: null, onerr: null, failed: false };
    w.onmessage = (e) => (st.onmsg ? st.onmsg(e) : e.data?.type === "ready" || e.data?.type === "fail" ? (st.msg = e) : null);
    w.onerror = (e) => {
      if (st.onerr) st.onerr(e);
      else st.failed = true;
    };
    w.postMessage({ type: "init" });
    prewarmed = st;
  } catch {
    prewarmed = null;
  }
}

export function createBarcodeScanner({ video, onResult, getBand = null }) {
  let stopped = true;
  let timer = null;
  let attempts = 0;
  let detector = null;
  let reader = null;
  let canvas = null;
  let formatName = null;
  // Off-main-thread decoding. `worker` is set only while it is healthy; any
  // failure drops to the main-thread path below, which is the original code.
  let worker = null;
  let workerEngine = null;
  let workerFailed = false;
  let nextId = 0;
  let pending = null; // { id, resolve, timeout } — at most one frame in flight
  let inFlight = false;
  let quietUntil = 0; // performance.now() before which no frame is grabbed
  const onTouch = () => {
    quietUntil = performance.now() + TOUCH_QUIET_MS;
  };
  // For the scan_perf step log: looks and their cost, and the preview frames
  // painted while scanning, so a slow phone reports the frame rate it showed.
  const stats = { ticks: 0, tickMs: 0, maxTickMs: 0, frames: 0, runMs: 0 };
  let runFrom = 0;
  let frameLoop = 0; // a stop-start must not leave two frame counters running
  const countFrames = (loop) => {
    if (stopped || loop !== frameLoop) return;
    stats.frames += 1;
    video.requestVideoFrameCallback(() => countFrames(loop));
  };
  // One per pass; see createConfirmer.
  const confirmers = { band: createConfirmer(), full: createConfirmer() };

  async function decodeNative(full) {
    if (!detector || !canvas) return null;
    // The CANVAS, not the <video>.
    //
    // detect(video) handed the detector the raw sensor frame — 3840x2160, 8.3
    // megapixels, with a GPU-to-CPU readback of roughly 33MB, every single
    // tick. The camera is opened at that resolution because the same stream
    // takes the photographs; nothing says the barcode reader has to pay for it.
    // BarcodeDetector accepts any CanvasImageSource, so it now reads the same
    // cropped, downscaled band the ZXing path does — about a tenth of the
    // pixels, centred on where the merchant is aiming.
    if (!drawFrame(video, canvas, { full, band: getBand?.() || null })) return null;
    try {
      const results = await detector.detect(canvas);
      const hit = nearestCentre(results, canvas);
      return hit?.rawValue ? { code: hit.rawValue, format: hit.format || null } : null;
    } catch {
      // Chromium throws here if the frame is not ready or the tab backgrounded.
      // Next tick will do.
      return null;
    }
  }

  function decodeZxing(full) {
    if (!reader || !canvas) return null;
    if (!drawFrame(video, canvas, { full, band: getBand?.() || null })) return null;

    // STRAIGHT FROM THE CANVAS. This used to encode the canvas to a JPEG, wrap
    // it in a blob URL, hand that to decodeFromImageUrl, wait for an <img> to
    // load (a full JPEG decode), and let ZXing allocate a SECOND canvas and
    // draw into it — all to reach pixels we already had. `decodeFromCanvas` is
    // synchronous and takes the canvas directly. Removing the round trip is
    // most of the per-attempt cost on iOS, where this is the only path.
    try {
      const hit = reader.decodeFromCanvas(canvas);
      const code = hit?.getText();
      // ZXing's format is an enum number; its name lowercased is the
      // BarcodeDetector spelling (EAN_13 -> ean_13).
      return code ? { code, format: formatName?.(hit.getBarcodeFormat()) || null } : null;
    } catch {
      // ZXing throws NotFoundException on every frame without a barcode, which
      // is most of them.
      return null;
    }
  }

  const WORKER_REPLY_MS = 8000;

  function workerUsable() {
    // A page with the platform reader keeps it: Chrome on Android has no
    // BarcodeDetector inside a worker, so the worker would fall back to ZXing,
    // slower to load and to read. The worker is for ZXing phones (iPhone).
    if (hasNativeDetector()) return false;
    return (
      !workerFailed &&
      typeof Worker === "function" &&
      typeof createImageBitmap === "function" &&
      typeof OffscreenCanvas === "function"
    );
  }

  function dropWorker() {
    workerFailed = true;
    workerEngine = null;
    if (worker) {
      worker.terminate();
      worker = null;
    }
    if (pending) {
      clearTimeout(pending.timeout);
      pending.resolve(undefined); // undefined = the worker died, not "no barcode"
      pending = null;
    }
  }

  // Starts the worker and resolves true once it has a decoder.
  function startWorker() {
    return new Promise((resolve) => {
      let w;
      const pre = prewarmed;
      prewarmed = null;
      try {
        w = pre ? pre.w : new Worker(new URL("./barcodeWorker.js", import.meta.url), { type: "module" });
      } catch {
        resolve(false);
        return;
      }
      const giveUp = setTimeout(() => {
        w.terminate();
        resolve(false);
      }, 15000);
      w.onerror = () => {
        clearTimeout(giveUp);
        if (worker === w) dropWorker();
        else w.terminate();
        resolve(false);
      };
      w.onmessage = (e) => {
        const m = e.data;
        if (m.type === "ready") {
          clearTimeout(giveUp);
          worker = w;
          workerEngine = m.engine;
          resolve(true);
        } else if (m.type === "fail") {
          clearTimeout(giveUp);
          w.terminate();
          resolve(false);
        } else if (m.type === "result" && pending && pending.id === m.id) {
          clearTimeout(pending.timeout);
          const { resolve: done } = pending;
          pending = null;
          done(m.read);
        }
      };
      if (pre) {
        // Already initialising since the camera was requested: take over its
        // handlers and replay whatever it said before we were listening.
        pre.onmsg = w.onmessage;
        pre.onerr = w.onerror;
        if (pre.failed) w.onerror();
        else if (pre.msg) w.onmessage(pre.msg);
      } else {
        w.postMessage({ type: "init" });
      }
    });
  }

  // One frame through the worker. Resolves to a read, null (nothing found or
  // frame not ready) or undefined (the worker failed: use the main thread).
  async function decodeInWorker(full) {
    const r = sourceRect(video.videoWidth, video.videoHeight, { full, band: getBand?.() || null });
    if (!r) return null;
    let bitmap;
    try {
      bitmap = await createImageBitmap(video, r.sx, r.sy, r.sw, r.sh, {
        resizeWidth: r.w,
        resizeHeight: r.h,
        resizeQuality: "high",
      });
    } catch {
      return null; // frame not ready; next tick will do
    }
    if (stopped || !worker) {
      bitmap.close?.();
      return stopped ? null : undefined;
    }
    return new Promise((resolve) => {
      const id = ++nextId;
      const timeout = setTimeout(dropWorker, WORKER_REPLY_MS);
      pending = { id, resolve, timeout };
      try {
        worker.postMessage({ type: "frame", id, bitmap, w: r.w, h: r.h }, [bitmap]);
      } catch {
        dropWorker();
      }
    });
  }

  // Builds the main-thread decoder (BarcodeDetector, else ZXing). False when
  // there is none at all.
  async function initMainDecoder() {
    // BOTH paths need the canvas now — the native detector reads the cropped
    // band from it rather than being handed the 4K <video>.
    if (!canvas) canvas = document.createElement("canvas");

    // Built once and reused across every product. It used to be constructed
    // on each start(), so any warm-up was paid again for every barcode.
    if (!detector && !reader && hasNativeDetector()) {
      detector = await makeNativeDetector();
    }

    if (!detector && !reader) {
      // Lazy: ZXing is a substantial parse, and a Chromium phone never needs
      // it. Loading it up front would tax the devices that do not use it.
      try {
        const [{ BrowserMultiFormatReader }, { BarcodeFormat, DecodeHintType }] =
          await Promise.all([import("@zxing/browser"), import("@zxing/library")]);
        // WITH HINTS. See zxingHints — without them this reader runs five
        // decoders over every frame and cannot read a rotated barcode at all.
        reader = new BrowserMultiFormatReader(zxingHints(BarcodeFormat, DecodeHintType));
        formatName = (f) => BarcodeFormat[f]?.toLowerCase() || null;
      } catch {
        // No decoder at all. The caller's manual path is the only way through;
        // we simply never fire a result.
        return false;
      }
    }
    return true;
  }

  async function tick() {
    if (stopped) return;

    // One frame in flight at a time: a stop()/start() while a decode is still
    // pending must not spawn a second loop.
    if (inFlight) {
      timer = setTimeout(tick, INTERVAL_NATIVE_MS);
      return;
    }
    // A finger just went down: let its handler and paint run first.
    const quiet = quietUntil - performance.now();
    if (quiet > 0) {
      timer = setTimeout(tick, quiet);
      return;
    }
    inFlight = true;
    const startedAt = Date.now();
    // Every FULL_FRAME_EVERY-th attempt ignores the centre band. The crop is
    // what makes the common case fast; this is what stops it becoming a
    // requirement to aim perfectly.
    attempts += 1;
    const full = attempts % FULL_FRAME_EVERY === 0;

    let read;
    try {
      read = worker ? await decodeInWorker(full) : undefined;
      if (read === undefined && !worker) {
        // No worker, or it just failed: the original main-thread path.
        if (!detector && !reader && !(await initMainDecoder())) {
          stopped = true;
          return;
        }
        read = detector ? await decodeNative(full) : decodeZxing(full);
      }
    } finally {
      inFlight = false;
    }

    // Re-check `stopped`: an await inside a loop the caller may have stopped is
    // exactly how a locked barcode gets overwritten a beat later.
    if (stopped) return;

    const code = read && String(read.code).trim();
    const confirmer = full ? confirmers.full : confirmers.band;
    // Monotonic: a wall-clock step would stretch or break the confirm window.
    const locked = code ? confirmer.push({ code, format: read.format }, performance.now()) : null;
    if (locked) {
      stats.runMs += performance.now() - runFrom;
      stopped = true;
      // Logged so a floor test can say which format a wrong code came from.
      console.info(`[barcode] locked ${locked.code} (${locked.format || "unknown"})`);
      onResult(locked.code, { format: locked.format, attempts });
      return;
    }

    // ⚠️ SCHEDULED FROM THE START OF THE TICK, NOT THE END. It used to be
    // setTimeout(tick, GAP) after awaiting the work, which made the real period
    // `work + GAP` — and on the ZXing path the work was the larger term, so the
    // nominal 600ms was closer to 900. Subtracting the elapsed time makes the
    // interval mean what it says; a tick that overruns simply reschedules
    // immediately rather than compounding.
    const gap = (worker ? workerEngine === "native" : detector) ? INTERVAL_NATIVE_MS : INTERVAL_ZXING_MS;
    const cost = Date.now() - startedAt;
    stats.ticks += 1;
    stats.tickMs += cost;
    stats.maxTickMs = Math.max(stats.maxTickMs, cost);
    const wait = Math.min(MAX_GAP_MS, Math.max(gap, cost * DUTY_FACTOR));
    timer = setTimeout(tick, Math.max(0, wait - cost));
  }

  return {
    /** Looks, their cost and the preview frames painted, since the last start(). */
    get stats() {
      return { ...stats, runMs: stats.runMs + (stopped ? 0 : performance.now() - runFrom) };
    },

    /** Which decoder is live. Surfaced so the UI can say why scanning is slow. */
    get engine() {
      if (worker) return workerEngine === "native" ? "native" : "zxing";
      if (stopped && !detector && !reader) return null;
      return detector ? "native" : "zxing";
    },

    async start() {
      if (!stopped) return;
      stopped = false;
      attempts = 0;
      if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
        document.addEventListener("pointerdown", onTouch, { capture: true, passive: true });
        document.addEventListener("touchstart", onTouch, { capture: true, passive: true });
      }
      confirmers.band.reset();
      confirmers.full.reset();
      Object.assign(stats, { ticks: 0, tickMs: 0, maxTickMs: 0, frames: 0, runMs: 0 });
      runFrom = performance.now();
      const loop = ++frameLoop;
      video.requestVideoFrameCallback?.(() => countFrames(loop));

      if (!worker && workerUsable()) {
        if (!(await startWorker())) workerFailed = true;
      }
      if (!worker && !(await initMainDecoder())) {
        stopped = true;
        return;
      }

      if (stopped) return;
      tick();
    },

    stop() {
      if (!stopped) stats.runMs += performance.now() - runFrom;
      stopped = true;
      if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
        document.removeEventListener("pointerdown", onTouch, { capture: true });
        document.removeEventListener("touchstart", onTouch, { capture: true });
      }
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    },

    /** Stop and free the worker. A scanner is made per camera session. */
    dispose() {
      this.stop();
      if (worker) {
        worker.terminate();
        worker = null;
      }
      if (pending) {
        clearTimeout(pending.timeout);
        pending.resolve(null);
        pending = null;
      }
    },
  };
}
